import { Link } from "react-router-dom";
import { TrendingUp, Shield, Wallet, LineChart, Brain, FileText, BarChart2, ArrowRight, ChevronRight } from "lucide-react";

const services = [
  {
    id: "nebula-fx",
    title: "Real-Time Market Intelligence",
    subtitle: "Nebula FX",
    description: "Live FX rates, exposure tracking, hedge recommendations, market alerts.",
    icon: TrendingUp,
    color: "from-indigo-500 to-purple-600",
  },
  {
    id: "nebula-ai",
    title: "AI-Powered Financial Insights",
    subtitle: "Intelligence Platform",
    description: "Exposure risk analysis, benchmarking, debt refinancing suggestions, market predictions.",
    icon: Brain,
    color: "from-pink-500 to-rose-600",
  },
  {
    id: "Treasury-reporting",
    title: "Treasury Reporting",
    subtitle: "AI-Powered Reports",
    description: "Enterprise wide Treasury Reporting powered by Nebula Intelligence.",
    icon: FileText,
    color: "from-violet-500 to-indigo-600"
  },
  {
    id: "hedge-solutions",
    title: "Hedge Solutions",
    subtitle: "Risk Management",
    description: "Comprehensive hedging strategies to protect your business from currency volatility.",
    icon: Shield,
    color: "from-emerald-500 to-teal-600",
  },
  {
    id: "working-capital",
    title: "Debt Management",
    subtitle: "Business Finance",
    description: "Flexible financing solutions to optimize your cash flow and manage debt effectively.",
    icon: Wallet,
    color: "from-amber-500 to-orange-600",
  },
  {
    id: "investments",
    title: "Investments",
    subtitle: "Wealth Management",
    description: "Tailored investment strategies designed to grow and preserve your wealth.",
    icon: LineChart,
    color: "from-cyan-500 to-blue-600",
  },
  {
    id: "receivables-intelligence",
    title: "Receivables Intelligence",
    subtitle: "Collections Analytics",
    description: "DSO tracking, weighted days-to-pay, cash flow impact, and annual income loss per counterparty.",
    icon: BarChart2,
    color: "from-teal-500 to-cyan-600",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-white font-bold text-lg">N</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Nebula Finance AI</h1>
              <p className="text-xs text-gray-400 -mt-1">Finance Intelligence Platform</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            {/* About Us — replaces old Log In */}
            <Link
              to="/about"
              className="px-5 py-2.5 text-gray-300 hover:text-white transition-colors font-medium"
            >
              About Us
            </Link>
            {/* Proprietary Software — replaces old Get Started */}
            <Link
              to="/platform"
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all hover:-translate-y-0.5"
            >
              Build Your Platform
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-sm text-indigo-300">Live Markets Active</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Elevate Your
            <span className="block bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Financial Strategy
            </span>
          </h1>

          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Nebula Finance AI delivers comprehensive financial solutions
            for businesses seeking excellence in FX, treasury, and wealth management — built by Treasury Experts.
          </p>

          {/* Single CTA — Log In — with regional sub-options handled inside LoginPage */}
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/login"
              className="nebula-btn-primary flex items-center gap-2 text-lg px-8 py-4"
            >
              Log In <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Comprehensive financial solutions tailored to your business needs
            </p>
          </div>

          <div className="service-grid">
            {services.map((service) => (
              <Link to="/login" key={service.id} className="nebula-card p-8 group block">
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                >
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{service.title}</h3>
                <p className="text-sm text-indigo-400 mb-3">{service.subtitle}</p>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.description}</p>
                <div className="flex items-center text-indigo-400 text-sm font-medium group-hover:text-indigo-300">
                  Explore <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#2a2d3a]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-white font-bold">N</span>
            </div>
            <div>
              <p className="text-white font-semibold">Nebula Finance AI</p>
              <p className="text-gray-500 text-sm">Excellence in Financial Solutions</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm">© 2026 Nebula Finance AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
